import type {
  ClientOverviewDTO,
  ClientOverviewDTOPartnerApproval,
  ClientOverviewMarketingChannelsDTOChannelsItem,
  ClientOverviewMarketingChannelsDTODigitalChannelsItem,
  ClientOverviewMarketingChannelsDTOFormDataCapturePageItem,
  ClientOverviewMarketingChannelsDTOPrintItem,
} from "generated/schemas";

export type GeneralDetailsFormValues = {
  name: string;
  partnerApproval: string;
  coreTeam: {
    contentDirector: string;
    projectCoordinator: string;
    accountManager: string;
    projectManager: string;
    copyWriter: string;
    campaignManager: string;
    designer: string;
    directorOfClientSuccess: string;
  };
  marketingChannels: {
    channels: string[];
    formDataCapturePage: string[];
    digitalChannels: string[];
    print: string[];
    formDataCapturePageOtherDetails: string;
    printOtherDetails: string;
  };
};

export const OTHER_OPTION_VALUE = "other";

export type GeneralDetailsOption<TValue extends string = string> = { label: string; value: TValue };

/** Verbatim options/labels from the removed campaign-template `general-details` schema block. */
export const PARTNER_APPROVAL_OPTIONS: GeneralDetailsOption<ClientOverviewDTOPartnerApproval>[] = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  {
    label: "Strategy and creative to be presented together for partner approval",
    value: "strategy-and-creative-together",
  },
];

export const MARKETING_CHANNELS_OPTIONS: GeneralDetailsOption<ClientOverviewMarketingChannelsDTOChannelsItem>[] = [
  { label: "Email", value: "email" },
  { label: "SMS", value: "sms" },
  { label: "Web page", value: "web-page" },
];

export const FORM_DATA_CAPTURE_PAGE_OPTIONS: GeneralDetailsOption<ClientOverviewMarketingChannelsDTOFormDataCapturePageItem>[] =
  [
    { label: "SMS", value: "sms" },
    { label: "Parent email", value: "parent-email" },
    { label: "Digital ad", value: "digital-ad" },
    { label: "Parent digital ad", value: "parent-digital-ad" },
    { label: "Other", value: "other" },
  ];

/** Renamed from the schema's `advertising` field — same option values, new label. */
export const DIGITAL_CHANNELS_OPTIONS: GeneralDetailsOption<ClientOverviewMarketingChannelsDTODigitalChannelsItem>[] = [
  { label: "Meta ads", value: "meta-ads" },
  { label: "Google display ads", value: "google-display-ads" },
  { label: "Google SEM ads", value: "google-sem-ads" },
  { label: "TikTok ads", value: "tiktok-ads" },
  { label: "YouTube ads", value: "youtube-ads" },
  { label: "Connections", value: "connections" },
];

export const PRINT_OPTIONS: GeneralDetailsOption<ClientOverviewMarketingChannelsDTOPrintItem>[] = [
  { label: "Postcard", value: "postcard" },
  { label: "Letter package", value: "letter-package" },
  { label: "Mailer", value: "mailer" },
  { label: "Other", value: "other" },
];

const toDtoValue = <TValue extends string>(
  options: GeneralDetailsOption<TValue>[],
  value: string,
): TValue | undefined => options.find((option) => option.value === value)?.value;

const toDtoValues = <TValue extends string>(options: GeneralDetailsOption<TValue>[], values: string[]): TValue[] =>
  values.filter((value): value is TValue => options.some((option) => option.value === value));

export const toOverviewDto = (values: GeneralDetailsFormValues): ClientOverviewDTO => ({
  partnerApproval: toDtoValue(PARTNER_APPROVAL_OPTIONS, values.partnerApproval),
  coreTeam: values.coreTeam,
  marketingChannels: {
    channels: toDtoValues(MARKETING_CHANNELS_OPTIONS, values.marketingChannels.channels),
    formDataCapturePage: toDtoValues(FORM_DATA_CAPTURE_PAGE_OPTIONS, values.marketingChannels.formDataCapturePage),
    digitalChannels: toDtoValues(DIGITAL_CHANNELS_OPTIONS, values.marketingChannels.digitalChannels),
    print: toDtoValues(PRINT_OPTIONS, values.marketingChannels.print),
    formDataCapturePageOtherDetails:
      values.marketingChannels.formDataCapturePageOtherDetails.trim() || undefined,
    printOtherDetails: values.marketingChannels.printOtherDetails.trim() || undefined,
  },
});
